import React, { useEffect } from 'react';

import type { Site, Procedure, SiteLink } from './entities';

import ElementHeader from '@helsenorge/designsystem-react/components/ElementHeader';
import Expander from '@helsenorge/designsystem-react/components/Expander';
import HighlightPanel from '@helsenorge/designsystem-react/components/HighlightPanel';
import LinkList from '@helsenorge/designsystem-react/components/LinkList';
import Loader from '@helsenorge/designsystem-react/components/Loader';
import NotificationPanel from '@helsenorge/designsystem-react/components/NotificationPanel';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';
import type { TitleTags } from '@helsenorge/designsystem-react/components/Title';

import { getSitesAsync, getProceduresAsync, deduplicateSiteLinks } from './utils';

interface FnspTreatmentBlockProps {
  snomedCodes: number[];
  showLocations: string;
  title: string;
  titleHtmlMarkup: TitleTags;
  description: string;
}

const FnspTreatmentBlock: React.FunctionComponent<FnspTreatmentBlockProps> = props => {
  const [sites, setSites] = React.useState<Site[]>([]);
  const [procedures, setProcedures] = React.useState<Procedure[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loaded, setLoaded] = React.useState(false);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const snomedCodes = props.snomedCodes.map(x => x.toString(10));
      try {
        const [sites, ...procedures] = await Promise.all([getSitesAsync(), ...snomedCodes.map(getProceduresAsync)]);

        setSites(sites);
        setProcedures(procedures.flat());
        setLoaded(true);
      } catch (error) {
        if (error instanceof Error) {
          setError('Klarte ikke å hente data: ' + error.message);
        } else {
          setError('Ukjent feil under henting av data');
        }
      }
    };

    fetchData();
  }, [props.snomedCodes]);

  const siteLinks: SiteLink[] = procedures
    .map(procedure => {
      const site = sites.find(s => s.nettstedid === procedure.siteId);
      if (site) {
        const siteLink: SiteLink = { href: procedure.webpageUrl, title: site.tittel, description: procedure.title, id: site.nettstedid };

        return siteLink;
      }

      return null;
    })
    .filter(site => !!site)
    .reduce<SiteLink[]>(deduplicateSiteLinks, [])
    .sort((a, b) => a.title.localeCompare(b.title));

  if (error) {
    return <NotificationPanel variant="error">{error}</NotificationPanel>;
  }

  if (!loaded) {
    return <Loader />;
  }

  if (siteLinks.length === 0) {
    return null;
  }

  return (
    <HighlightPanel title={props.title} color="blueberry" titleHtmlMarkup={props.titleHtmlMarkup}>
      {props.description}
      <Spacer />
      <Expander title={props.showLocations.replace('{{count}}', siteLinks.length.toString(10))} noNestedLine>
        <LinkList color="blueberry" chevron>
          {siteLinks.map(link => (
            <LinkList.Link key={link.id} href={link.href}>
              <ElementHeader>
                <ElementHeader.Text firstText={link.title} firstTextEmphasised />
                <ElementHeader.Text firstText={link.description} />
              </ElementHeader>
            </LinkList.Link>
          ))}
        </LinkList>
      </Expander>
    </HighlightPanel>
  );
};

export default FnspTreatmentBlock;
